"use client";

/**
 * Tử Vi Số · ChartGrid (lá số 12 cung)
 *
 * Layout: 4×4 grid where the 4 corner cells + 8 edge cells are palaces,
 * and the center 2×2 is the "Mệnh chủ" info card.
 *
 * Standard Vietnamese Tử Vi layout has Tỵ-Ngọ-Mùi-Thân across the top row
 * and Tý-Sửu (right) / Hợi-Tuất (left) wrapping clockwise. We pin each
 * earthly branch to a fixed (col, row) so palace order from iztro doesn't
 * matter — we always render the same physical layout.
 *
 *   ┌─────┬─────┬─────┬─────┐
 *   │ Tỵ  │ Ngọ │ Mùi │ Thân│
 *   ├─────┼─────┴─────┼─────┤
 *   │ Thìn│           │ Dậu │
 *   ├─────┤  CENTER   ├─────┤
 *   │ Mão │           │ Tuất│
 *   ├─────┼─────┬─────┼─────┤
 *   │ Dần │ Sửu │ Tý  │ Hợi │
 *   └─────┴─────┴─────┴─────┘
 */

import { useState } from "react";
import { cn } from "@/lib/utils";
import type { Chart, Palace, Star } from "@/lib/tuvi/engine";
import { isMajorStar } from "@/lib/tuvi/major-stars-catalog";
import { MajorStarModal } from "./major-star-modal";

type Pos = { col: 1 | 2 | 3 | 4; row: 1 | 2 | 3 | 4 };

const BRANCH_POSITIONS: Record<string, Pos> = {
  Tỵ: { col: 1, row: 1 },
  Ngọ: { col: 2, row: 1 },
  Mùi: { col: 3, row: 1 },
  Thân: { col: 4, row: 1 },
  Thìn: { col: 1, row: 2 },
  Dậu: { col: 4, row: 2 },
  Mão: { col: 1, row: 3 },
  Tuất: { col: 4, row: 3 },
  Dần: { col: 1, row: 4 },
  Sửu: { col: 2, row: 4 },
  Tý: { col: 3, row: 4 },
  Hợi: { col: 4, row: 4 },
};

function MutagenBadge({ mutagen }: { mutagen: string }) {
  if (!mutagen) return null;
  // Lộc=green, Quyền=blue, Khoa=gold, Kỵ=red
  const tone =
    mutagen === "Lộc"
      ? "bg-emerald-700/40 text-emerald-200 border-emerald-600/40"
      : mutagen === "Quyền"
        ? "bg-sky-700/40 text-sky-200 border-sky-600/40"
        : mutagen === "Khoa"
          ? "bg-amber-700/40 text-amber-200 border-amber-600/40"
          : "bg-rose-700/40 text-rose-200 border-rose-600/40";
  return (
    <span
      className={cn("ml-1 inline-block rounded border px-1 text-[9px] leading-tight", tone)}
      title={`Hóa ${mutagen}`}
    >
      {mutagen}
    </span>
  );
}

function StarLine({ star, onClick }: { star: Star; onClick?: (name: string) => void }) {
  const colorClass =
    star.kind === "major"
      ? "text-brand-cream font-medium"
      : star.kind === "minor"
        ? "text-brand-gold-soft"
        : "text-ink-muted";
  const clickable = star.kind === "major" && isMajorStar(star.name) && onClick;
  return (
    <div className={cn("text-[11px] leading-snug", colorClass)}>
      {clickable ? (
        <button
          type="button"
          onClick={() => onClick(star.name)}
          className="hover:text-brand-gold cursor-pointer underline-offset-2 hover:underline"
          title={`Xem chi tiết sao ${star.name}`}
        >
          {star.name}
        </button>
      ) : (
        <span>{star.name}</span>
      )}
      {star.brightness ? (
        <span className="text-ink-muted ml-1 text-[9px]">[{star.brightness}]</span>
      ) : null}
      <MutagenBadge mutagen={star.mutagen ?? ""} />
    </div>
  );
}

function PalaceCellView({
  palace,
  onStarClick,
}: {
  palace: Palace | undefined;
  onStarClick: (name: string) => void;
}) {
  if (!palace) {
    return <div className="palace-cell h-full rounded p-2 text-xs opacity-30" />;
  }
  return (
    <div
      className={cn(
        "palace-cell flex h-full flex-col rounded p-2 text-xs lg:p-3",
        palace.isBodyPalace && "border-brand-gold/50 bg-bg-overlay/60"
      )}
    >
      {/* Top: palace name + body marker */}
      <div className="flex items-center justify-between">
        <span className="font-display text-brand-gold text-[11px] font-medium tracking-wide lg:text-xs">
          {palace.name}
        </span>
        {palace.isBodyPalace ? (
          <span className="bg-brand-red/30 text-brand-cream rounded px-1 text-[8px] tracking-widest uppercase">
            Thân
          </span>
        ) : null}
      </div>

      {/* Stars stack */}
      <div className="mt-1 flex flex-1 flex-col gap-0.5">
        {palace.majorStars.map((s) => (
          <StarLine key={`maj-${s.name}`} star={s} onClick={onStarClick} />
        ))}
        {palace.minorStars.map((s) => (
          <StarLine key={`min-${s.name}`} star={s} onClick={onStarClick} />
        ))}
        {palace.adjStars.slice(0, 4).map((s) => (
          <StarLine key={`adj-${s.name}`} star={s} onClick={onStarClick} />
        ))}
      </div>

      {/* Bottom: stem-branch */}
      <div className="text-ink-muted mt-1 flex items-center justify-end gap-1 text-[9px] tracking-wider uppercase">
        <span>{palace.heavenlyStem}</span>
        <span className="text-brand-gold-soft">{palace.earthlyBranch}</span>
      </div>
    </div>
  );
}

function CenterCard({ chart }: { chart: Chart }) {
  return (
    <div className="palace-cell bg-bg-overlay/80 border-brand-gold/30 col-start-2 col-end-4 row-start-2 row-end-4 flex flex-col items-center justify-center rounded p-4 text-center lg:p-6">
      <div className="text-ink-muted text-[10px] tracking-[0.18em] uppercase">Mệnh Chủ</div>
      <div className="font-display text-brand-gold mt-1 text-2xl lg:text-3xl">{chart.soulStar}</div>
      <div className="text-ink-muted mt-1 text-[10px] tracking-[0.15em] uppercase">
        Thân Chủ · {chart.bodyStar}
      </div>

      <div className="bg-stroke-subtle my-3 h-px w-12" />

      {chart.fullName ? (
        <div className="font-display text-ink-primary text-base lg:text-lg">{chart.fullName}</div>
      ) : null}
      <div className="text-ink-secondary mt-1 text-xs">
        {chart.gender} · {chart.zodiac} · {chart.sign}
      </div>
      <div className="font-numeral text-ink-primary mt-2 text-xl lg:text-2xl">
        {chart.solarDate}
      </div>
      <div className="text-ink-muted mt-0.5 text-[10px]">{chart.lunarDate}</div>
      <div className="text-ink-muted mt-1 text-[10px]">{chart.timeRange}</div>

      <div className="bg-stroke-subtle my-3 h-px w-12" />

      <div className="text-brand-gold-soft font-display text-sm">{chart.fiveElements}</div>
      <div className="text-ink-muted mt-0.5 text-[10px]">{chart.chineseDate}</div>
    </div>
  );
}

export function ChartGrid({ chart }: { chart: Chart }) {
  const [activeStar, setActiveStar] = useState<string | null>(null);

  // Index palaces by branch for O(1) layout lookup.
  const byBranch = new Map<string, Palace>();
  chart.palaces.forEach((p) => byBranch.set(p.earthlyBranch, p));

  return (
    <>
      <div className="border-stroke-subtle bg-bg-elevated/40 grid auto-rows-fr grid-cols-4 grid-rows-4 gap-1 rounded-2xl border p-2 backdrop-blur-sm lg:gap-2 lg:p-3">
        {Object.entries(BRANCH_POSITIONS).map(([branch, pos]) => (
          <div
            key={branch}
            style={{ gridColumn: pos.col, gridRow: pos.row }}
            className="min-h-[140px] lg:min-h-[200px]"
          >
            <PalaceCellView palace={byBranch.get(branch)} onStarClick={setActiveStar} />
          </div>
        ))}
        <CenterCard chart={chart} />
      </div>
      <p className="text-ink-muted mt-2 text-center text-[11px] italic lg:text-xs">
        💡 Click vào tên các chính tinh (sao tô đậm) để xem ý nghĩa khi tọa cung Mệnh và 11 cung
        khác
      </p>
      {activeStar ? (
        <MajorStarModal starName={activeStar} onClose={() => setActiveStar(null)} />
      ) : null}
    </>
  );
}
